import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/chadcn/Dialog"
import { DateTimeFormatter, DayOfWeek, LocalDate, ZoneId, ZonedDateTime } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { CalendarIcon } from "@radix-ui/react-icons"
import { useState } from "react"
import MonthCalendar from "./MonthCalendar"

type WeeklyCalendarProps = {
  weekStart: ZonedDateTime
  onWeekChange: (newWeekStart: ZonedDateTime) => void
}

const RANGE_FORMAT = DateTimeFormatter.ofPattern("MMM dd").withLocale(Locale.US)

const weekStartForDate = (date: LocalDate): ZonedDateTime => {
  const daysFromSunday = date.dayOfWeek().value() % DayOfWeek.SUNDAY.value()
  return date.minusDays(daysFromSunday).atStartOfDay(ZoneId.systemDefault())
}

const WeeklyCalendar = ({ weekStart, onWeekChange }: WeeklyCalendarProps) => {
  const [open, setOpen] = useState(false)
  const [draftWeekStart, setDraftWeekStart] = useState(weekStart.toLocalDate())
  const weekEnd = weekStart.plusDays(6)
  const headerLabel = `${weekStart.toLocalDate().format(RANGE_FORMAT)} - ${weekEnd.toLocalDate().format(RANGE_FORMAT)}`

  const handleOpen = () => {
    setDraftWeekStart(weekStart.toLocalDate())
    setOpen(true)
  }

  const handlePickDay = (day: LocalDate) => {
    const daysFromSunday = day.dayOfWeek().value() % DayOfWeek.SUNDAY.value()
    setDraftWeekStart(day.minusDays(daysFromSunday))
  }

  const handleApply = () => {
    onWeekChange(weekStartForDate(draftWeekStart))
    setOpen(false)
  }

  return (
    <div className="mt-6 flex flex-col gap-2">
      <p className="text-sm text-primary-contrast/80">Track your progress</p>
      <div className="flex items-center justify-between rounded-lg border border-secondary bg-white px-3 py-2">
        <span className="text-sm font-medium text-primary-contrast">{headerLabel}</span>
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Choose week"
          className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-contrast"
        >
          <CalendarIcon className="h-4 w-4" />
        </button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-sm [&>button.absolute]:hidden">
          <DialogHeader>
            <DialogTitle className="sr-only">Choose week</DialogTitle>
          </DialogHeader>
          <MonthCalendar selectedWeekStart={draftWeekStart} onPickWeekContaining={handlePickDay} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleApply}
              className="text-sm font-medium text-primary-contrast hover:underline"
            >
              Ok
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default WeeklyCalendar
